import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  fetchCta,
  fetchDaily,
  fetchEpisodes,
  fetchFunnel,
  fetchKpis,
  fetchSurfaces,
} from "@/components/SnippetEngagement/snippetEngagementServices";
import { isSnippetAdmin } from "@/composables/useSnippetApiToken";

const DEFAULT_RANGE_DAYS = 7;

const toDayString = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const defaultRange = () => {
  const today = new Date();
  const from = new Date();
  from.setDate(from.getDate() - DEFAULT_RANGE_DAYS);
  return [from, today];
};

const emptyCounts = () => ({
  snp_widget_viewed: 0,
  snp_widget_loads: 0,
  snp_in_view_3s: 0,
  snp_play: 0,
  snp_q4: 0,
  snp_unmute: 0,
  snp_scroll_left: 0,
  snp_scroll_right: 0,
  snp_fab_open: 0,
  snp_share: 0,
  snp_end_shown: 0,
  snp_end_dismissed: 0,
});

export const useSnippetEngagementStore = defineStore(
  "snippetEngagement",
  () => {
    const dateRange = ref(defaultRange());
    const memberFilter = ref({ partners: [], groups: [], sellers: [] });
    const viewAsSellerId = ref(null);
    const loading = ref({});

    const counts = ref(emptyCounts());
    const rates = ref({
      snp_unmute_rate: null,
      snp_share_rate: null,
      snp_completion_rate: null,
      snp_viewability: null,
    });
    const allowedKeys = ref(new Set());
    const showsCtaByType = ref(false);
    const cta = ref({
      snp_cta_listen_on: 0,
      snp_cta_spotify: 0,
      snp_cta_apple: 0,
    });
    const ctaIntent = ref({ wants_more: 0, listener_apps: 0 });
    const listenFullEpisode = ref(0);
    const funnel = ref({ plays: 0, milestones: [] });
    const surfaces = ref([]);
    const episodes = ref({ rows: [], totalRecords: 0 });
    const episodesParams = ref(null);
    const daily = ref({ rows: [], totalRecords: 0, columns: [] });
    const dailyParams = ref(null);
    const showsEpisodes = computed(() => isSnippetAdmin());

    const rangeParams = computed(() => ({
      startDate: toDayString(dateRange.value[0]),
      endDate: toDayString(dateRange.value[1] ?? dateRange.value[0]),
      memberFilter: JSON.stringify(memberFilter.value),
      ...(viewAsSellerId.value ? { asSellerId: viewAsSellerId.value } : {}),
    }));

    const isLoading = (key) => (loading.value[key] ?? 0) > 0;

    const isBusy = computed(() =>
      Object.values(loading.value).some((count) => count > 0)
    );

    const shows = (key) => allowedKeys.value.has(key);

    const track = async (key, run) => {
      loading.value = {
        ...loading.value,
        [key]: (loading.value[key] ?? 0) + 1,
      };
      try {
        return await run();
      } finally {
        loading.value = { ...loading.value, [key]: loading.value[key] - 1 };
      }
    };

    const loadKpis = () =>
      track("kpis", async () => {
        const data = await fetchKpis(rangeParams.value);
        if (!data) return;
        counts.value = data.counts;
        rates.value = data.rates;
        allowedKeys.value = new Set([
          ...Object.keys(data.counts),
          ...Object.keys(data.rates),
        ]);
      });

    const loadCta = () =>
      track("cta", async () => {
        const data = await fetchCta(rangeParams.value);
        if (!data) return;
        showsCtaByType.value = Boolean(data.cta);
        cta.value = data.cta ?? {};
        ctaIntent.value = data.intent;
        listenFullEpisode.value = data.listen_full_episode;
      });

    const loadFunnel = () =>
      track("funnel", async () => {
        const data = await fetchFunnel(rangeParams.value);
        if (data) funnel.value = data;
      });

    const loadSurfaces = () =>
      track("surfaces", async () => {
        const data = await fetchSurfaces(rangeParams.value);
        if (data) surfaces.value = data;
      });

    const loadEpisodes = (options = {}) =>
      !showsEpisodes.value
        ? Promise.resolve()
        : track("episodes", async () => {
            const params = { ...rangeParams.value, ...options };
            const data = await fetchEpisodes(params);
            if (data) {
              episodes.value = data;
              episodesParams.value = params;
            }
          });

    const exportEpisodes = () =>
      showsEpisodes.value && episodesParams.value
        ? fetchEpisodes({ ...episodesParams.value, page: 0, rows: 200 })
        : Promise.resolve(null);

    const loadDaily = (options = {}) =>
      track("daily", async () => {
        const params = { ...rangeParams.value, ...options };
        const data = await fetchDaily(params);
        if (data) {
          daily.value = data;
          dailyParams.value = params;
        }
      });

    const exportDaily = () =>
      dailyParams.value
        ? fetchDaily({ ...dailyParams.value, page: 0, rows: 200 })
        : Promise.resolve(null);

    const setDateRange = (value) => {
      dateRange.value = value;
    };

    const setViewAsSellerId = (value) => {
      viewAsSellerId.value = value || null;
    };

    const setMemberFilter = (value) => {
      memberFilter.value = { ...memberFilter.value, ...value };
    };

    const loadRange = () =>
      Promise.all([
        loadKpis(),
        loadCta(),
        loadFunnel(),
        loadSurfaces(),
        loadEpisodes(),
        loadDaily(),
      ]);

    return {
      dateRange,
      memberFilter,
      viewAsSellerId,
      counts,
      rates,
      shows,
      showsCtaByType,
      cta,
      ctaIntent,
      listenFullEpisode,
      funnel,
      surfaces,
      episodes,
      daily,
      showsEpisodes,
      isBusy,
      isLoading,
      setDateRange,
      setMemberFilter,
      setViewAsSellerId,
      loadRange,
      loadEpisodes,
      exportEpisodes,
      loadDaily,
      exportDaily,
    };
  }
);
